import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { CustomThemeProvider } from '@brainpool/themes';
import { Box, AppBar, Toolbar, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { CandidatesDashboard } from './pages/CandidatesDashboard';
import theme from './theme/theme';

type BootstrapResponse = {
  ok?: boolean;
  identity?: { email?: string; source?: string };
  clerk?: {
    status?: string;
    reason?: string | null;
    user?: { id?: string; email?: string | null } | null;
  };
  error?: string;
};

export function App() {
  const [bootstrapState, setBootstrapState] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const [bootstrapMessage, setBootstrapMessage] = useState('');

  const bootstrapAdminUser = async () => {
    setBootstrapState('loading');
    const apiUrl = ((import.meta.env as Record<string, string>)['VITE_API_URL'] ?? '').replace(
      /\/+$/,
      ''
    );
    const localDevEmail = (import.meta.env as Record<string, string>)['VITE_DEV_ADMIN_EMAIL'];
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (localDevEmail) headers['x-dev-admin-email'] = localDevEmail;

    try {
      const response = await fetch(`${apiUrl}/auth/admin/bootstrap`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(localDevEmail ? { email: localDevEmail } : {}),
      });
      const payload = (await response.json().catch(() => ({}))) as BootstrapResponse;
      if (!response.ok || payload.ok !== true) {
        setBootstrapState('error');
        setBootstrapMessage(payload.error ?? 'Failed to bootstrap admin user');
        return;
      }
      const identityEmail = payload.identity?.email ?? 'unknown';
      const clerkStatus = payload.clerk?.status ?? 'unknown';
      setBootstrapState('success');
      setBootstrapMessage(`${identityEmail} → Clerk ${clerkStatus}`);
    } catch (error) {
      setBootstrapState('error');
      setBootstrapMessage(String(error));
    }
  };

  const chipLabel = bootstrapState === 'loading' ? 'Bootstrapping...' : bootstrapMessage;

  return (
    <CustomThemeProvider theme={theme}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid #E2E2E2',
          borderTop: '3px solid #77C9B8',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: '56px !important', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <svg
              width="30"
              height="30"
              viewBox="0 0 512 512"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="256" cy="256" r="240" fill="#a2dacd" />
              <circle cx="256" cy="256" r="148" fill="#77cab7" />
              <circle cx="256" cy="256" r="91" fill="#76a5b2" />
              <circle cx="256" cy="256" r="58" fill="#615da9" />
            </svg>
            <Typography
              variant="h6"
              sx={{
                mb: 0,
                fontWeight: 300,
                color: '#A1A0A0',
                letterSpacing: '0.14em',
                fontSize: '0.875rem',
              }}
            >
              BRAINPOOL.AI
            </Typography>
          </Box>

          {bootstrapState !== 'idle' && (
            <Chip
              label={chipLabel}
              size="small"
              color={
                bootstrapState === 'success'
                  ? 'success'
                  : bootstrapState === 'error'
                    ? 'error'
                    : 'default'
              }
              icon={
                bootstrapState === 'loading' ? (
                  <CircularProgress size={12} color="inherit" />
                ) : undefined
              }
              sx={{ maxWidth: 400 }}
            />
          )}

          <Button
            size="small"
            variant="outlined"
            onClick={bootstrapAdminUser}
            disabled={bootstrapState === 'loading'}
            sx={{
              fontSize: 12,
              textTransform: 'none',
              borderColor: '#E2E2E2',
              color: '#6b6b6b',
              '&:hover': { borderColor: '#77C9B8', color: '#4a9787', bgcolor: 'transparent' },
            }}
          >
            Bootstrap Clerk
          </Button>
        </Toolbar>
      </AppBar>

      <Box component="main" data-testid="content">
        <Routes>
          <Route path="/*" element={<CandidatesDashboard />} />
        </Routes>
      </Box>
    </CustomThemeProvider>
  );
}

export default App;
